import { useSuperMutation, useSuperQuery } from '@vadiun/react-hooks-legacy';
import { useEffect, useState } from 'react';
import { EventModel } from '../models/Evento';
import useEventoService from '../services/useEventoService';
import {
  EventosColumnVisibility,
  EventosFilters,
  getDefaultFilters,
  loadStateFromLocalStorage,
  saveStateToLocalStorage
} from '../utils/eventosStorage';

export { getDefaultFilters, eventosColumnLabels } from '../utils/eventosStorage';
export type { EventosColumnVisibility } from '../utils/eventosStorage';

export const useEventos = () => {
  const eventoService = useEventoService();
  const [state, setState] = useState(loadStateFromLocalStorage);

  useEffect(() => {
    saveStateToLocalStorage(state);
  }, [state]);

  const eventosQuery = useSuperQuery(
    () =>
      eventoService.getEvents(
        state.page,
        state.rowsPerPage,
        state.filters.businessClientId,
        state.filters.fromDate,
        state.filters.toDate,
        state.filters.search
      ),
    { showSpinner: true },
    [
      state.page,
      state.rowsPerPage,
      JSON.stringify(state.filters)
    ]
  );

  const onFiltersChange = (filters: EventosFilters) => {
    setState((prev) => ({ ...prev, filters, page: 1 }));
  };

  const onChangePage = (page: number) => {
    setState((prev) => ({ ...prev, page }));
  };

  const onChangeRowsPerPage = (rowsPerPage: number) => {
    setState((prev) => ({ ...prev, rowsPerPage, page: 1 }));
  };

  const resetFilters = () => {
    setState((prev) => ({ ...prev, filters: getDefaultFilters(), page: 1 }));
  };

  const setColumnVisibility = (columnVisibility: EventosColumnVisibility) => {
    setState((prev) => ({ ...prev, columnVisibility }));
  };

  // PUT /events/:id no tiene un endpoint de "sólo activar/desactivar" — hay que
  // reenviar el evento completo (name/business_client_id/is_active) con
  // is_active invertido, tal como lo pide el backend.
  const toggleActiveMutation = useSuperMutation(
    (event: EventModel) =>
      eventoService.updateEvent(event.id, {
        name: event.name,
        description: event.description ?? undefined,
        business_client_id: event.businessClient.id,
        from_date: event.fromDate ?? undefined,
        to_date: event.toDate ?? undefined,
        is_active: !event.isActive
      }),
    {
      showSpinner: true,
      messages: { success: { message: 'Estado actualizado' } },
      onMutationFinish: (mutationState) => {
        if (!mutationState.isError) {
          eventosQuery.reload();
        }
      }
    }
  );

  const deleteEventMutation = useSuperMutation(
    (eventId: number) => eventoService.deleteEvent(eventId),
    {
      showSpinner: true,
      messages: { success: { message: 'Evento eliminado' } },
      onMutationFinish: (mutationState) => {
        if (!mutationState.isError) {
          eventosQuery.reload();
        }
      }
    }
  );

  return {
    events: eventosQuery.data?.data ?? [],
    total: eventosQuery.data?.meta?.total ?? 0,
    isLoading: eventosQuery.isLoading,
    reload: eventosQuery.reload,
    filters: state.filters,
    page: state.page,
    rowsPerPage: state.rowsPerPage,
    columnVisibility: state.columnVisibility,
    onFiltersChange,
    onChangePage,
    onChangeRowsPerPage,
    resetFilters,
    setColumnVisibility,
    toggleActive: toggleActiveMutation.mutate,
    deleteEvent: deleteEventMutation.mutate
  };
};
